import { Inject, Injectable } from '@angular/core';
import { SortMeta } from 'primeng/api';
import { catchError, combineLatest, filter, map, of, Subject, switchMap } from 'rxjs';
import { ColumnConfig } from '../../../../core/contract/user-configuration/column-config.contract';
import { ColumnSort } from '../../../../osp-ui/components/data/table/contract/column-sort.contract';
import { TableColumn } from '../../../../osp-ui/components/data/table/contract/table-column.contract';
import { PageableDatasource } from '../../../../shared/contract/pageable-datasource.contract';
import { PageableRequestParams } from '../../../../shared/contract/pageable-request-params.contract';
import { PagedResponse } from '../../../../shared/contract/paged-response.contract';
import { FilterServiceProvider } from '../../../../shared/contract/providers/filter-service-provider';
import { ListConfigServiceProvider } from '../../../../shared/contract/providers/list-config-service-provider.contract';
import { SearchServiceProvider } from '../../../../shared/contract/providers/search-service-provider.contract';
import { UpdateFilterCommand } from '../../../../shared/filters/contract/update-filter-command.contract';
import { FilterTypes } from '../../../../shared/filters/enums/filter-types.enum';
import { SearchService } from '../../../../shared/services/search.service';
import { toSortString } from '../../../../shared/utils/to-sort-string';
import { LocationsApiService } from '../../services/locations-api.service';
import { ActiveLocationsSearchService } from '../consts/injection-tokens.const';
import { ActiveLocation, SearchActiveLocationsRequest } from '../contract/active-location.contract';
import { ActiveLocationsFilterService } from './active-locations-filter.service';
import { ActiveLocationsListConfigService } from './active-locations-list-config.service';
import { ActiveLocationsSummaryService } from './active-locations-summary.service';

@Injectable({ providedIn: 'root' })
export class ActiveLocationsDatasourceService
  extends PageableDatasource<ActiveLocation>
  implements ListConfigServiceProvider, SearchServiceProvider, FilterServiceProvider
{
  readonly selectedColumns$ = this.listConfigService.selectedColumns$;
  readonly filters$ = this.activeLocationsFilterService.filters$;
  readonly selectedFilterTypes$ = this.activeLocationsFilterService.selectedFilterTypes$;
  readonly paramsInternalState$ = this.activeLocationsFilterService.paramsInternalState$;
  readonly isFiltersReady$ = this.activeLocationsFilterService.isFiltersReady$;
  readonly searchTerm$ = this.activeLocationsSearchService.searchTerm$;
  private readonly requestData$ = new Subject<PageableRequestParams>();

  get availableColumns(): TableColumn<ActiveLocation>[] {
    return this.listConfigService.availableColumns;
  }

  constructor(
    private readonly listConfigService: ActiveLocationsListConfigService,
    private readonly locationsApiService: LocationsApiService,
    private readonly activeLocationsFilterService: ActiveLocationsFilterService,
    private readonly activeLocationsSummaryService: ActiveLocationsSummaryService,
    @Inject(ActiveLocationsSearchService) private readonly activeLocationsSearchService: SearchService,
  ) {
    super();
    super.changePageSize(this.listConfigService.pageSize, false);
    this.subscribeToRequestData();

    const savedSort = this.listConfigService.sort;
    if (savedSort?.field) {
      super.changeSorting(savedSort, false);
    }
  }

  getSearchTerm(): string {
    return this.activeLocationsSearchService.getSearchTerm();
  }

  hasActiveFiltersOrSearch(): boolean {
    const { types, categories, costCenters, organisations, labels } =
      this.activeLocationsFilterService.getFilterParams();
    return (
      (types?.length ?? 0) > 0 ||
      (categories?.length ?? 0) > 0 ||
      (costCenters?.length ?? 0) > 0 ||
      (organisations?.length ?? 0) > 0 ||
      (labels?.length ?? 0) > 0 ||
      !!this.getSearchTerm()
    );
  }

  searchTermChange(term: string, update = true): void {
    this.activeLocationsSearchService.searchTermChange(term);
    if (update) {
      this.updateData({ page: 0 });
    }
  }

  protected requestData(params: PageableRequestParams): void {
    this.requestData$.next(params);
  }

  override changePageSize(size: number, update = true): void {
    this.listConfigService.savePageSize(size);
    super.changePageSize(size, update);
  }

  override changeSorting(incomingSort: ColumnSort | SortMeta, update = true): void {
    super.changeSorting(incomingSort, update);

    if (this.sort && !Array.isArray(this.sort)) {
      this.listConfigService.saveSort(this.sort as ColumnSort);
    }
  }

  changeColumnConfig(config: ColumnConfig): void {
    this.listConfigService.saveColumnConfigs(config);
  }

  // Filters
  initFilters(): void {
    this.activeLocationsFilterService.init();
  }

  updateFilters(): void {
    this.activeLocationsFilterService.updateFilters();
  }

  updateFilterParams(commands: UpdateFilterCommand[], updateFilters?: boolean, checkConsistency?: boolean): void {
    this.changePageIndex(0, false);
    this.activeLocationsFilterService.updateFilterParams(commands, updateFilters, checkConsistency);
    this.updateData();
  }

  showFilterTypes(types: FilterTypes[]): void {
    if (this.activeLocationsFilterService.showFilterTypes(types)) {
      this.updateData();
    }
  }

  resetFilters(defaultFilters: UpdateFilterCommand[] = [], updateFilters?: boolean, checkConsistency?: boolean): void {
    this.activeLocationsFilterService.resetFilters(defaultFilters, updateFilters, checkConsistency);
    this.updateData();
  }

  // List config
  getSelectedColumnsKeys(): string[] {
    return this.listConfigService.getSelectedColumnsKeys();
  }

  isDefaultColumn(columnKey: string): boolean {
    return this.listConfigService.isDefaultColumn(columnKey);
  }

  saveSelectedColumns(columnKeys: string[], update = true): void {
    this.listConfigService.saveSelectedColumns(columnKeys);

    if (update) {
      this.updateData();
    }
  }

  private defineRequestParams({ page, size, sort }: PageableRequestParams): SearchActiveLocationsRequest {
    const requestParams: SearchActiveLocationsRequest = {
      page,
      size,
      sort: toSortString(sort) || undefined,
      ...this.activeLocationsFilterService.getFilterParams(),
    };

    if (this.getSearchTerm()) {
      requestParams.term = this.getSearchTerm();
      requestParams.searchColumns = this.listConfigService.getSelectedColumnsKeys();
    }

    return requestParams;
  }

  private buildEmptyPagedResponse({ page, size }: PageableRequestParams): PagedResponse<ActiveLocation> {
    return {
      content: [],
      totalElements: 0,
      totalPages: 0,
      last: true,
      size,
      number: page,
      numberOfElements: 0,
      first: page === 0,
    };
  }

  private subscribeToRequestData(): void {
    this.requestData$
      .pipe(
        switchMap((params) =>
          this.activeLocationsFilterService.isFiltersReady$.pipe(
            filter((isReady) => isReady),
            map(() => params),
          ),
        ),
        map((params) => {
          const requestParams = this.defineRequestParams(params);
          const { page: _page, size: _size, sort: _sort, ...summaryRequest } = requestParams;
          return { params, requestParams, summaryRequest };
        }),
        switchMap(({ params, requestParams, summaryRequest }) =>
          combineLatest([
            this.locationsApiService
              .getActiveLocations(requestParams)
              .pipe(catchError(() => of(this.buildEmptyPagedResponse(params)))),
            this.locationsApiService.getActiveLocationsSummary(summaryRequest).pipe(catchError(() => of(null))),
          ]),
        ),
      )
      .subscribe(([response, summary]) => {
        this.setPageableData(response);
        this.activeLocationsSummaryService.$dataSummary.set(summary);
      });
  }
}
